import { format } from 'date-fns';

export interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  context?: Record<string, any>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000;
  private listeners: ((logs: LogEntry[]) => void)[] = [];
  private initialized: boolean = false;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.initialized = true;
      console.log('Logger initialized'); // Debug message
      this.info('Logger initialized', {
        maxLogs: this.maxLogs,
        timestamp: new Date().toISOString()
      });
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatTimestamp(): string {
    return format(new Date(), 'yyyy-MM-dd HH:mm:ss.SSS');
  }

  private addEntry(entry: LogEntry) {
    if (!this.initialized) {
      console.warn('Logger not initialized yet');
      return;
    }

    // Always log to console in development
    if (process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).get('devMode') === 'true') {
      const { level, message, context, error } = entry;
      console[level](
        `[${entry.timestamp}] ${message}`,
        context ? '\nContext:' : '',
        context || '',
        error ? '\nError:' : '',
        error || ''
      );
    }

    this.logs.unshift(entry);
    
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    this.notifyListeners();
  }

  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    this.listeners.push(listener);
    listener([...this.logs]); // Immediately send current logs
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener([...this.logs]);
      } catch (error) {
        console.error('Error in log listener:', error);
      }
    });
  }

  info(message: string, context?: Record<string, any>) {
    this.addEntry({
      timestamp: this.formatTimestamp(),
      level: 'info',
      message,
      context
    });
  }

  warn(message: string, context?: Record<string, any>) {
    this.addEntry({
      timestamp: this.formatTimestamp(),
      level: 'warn',
      message,
      context
    });
  }

  error(message: string, error?: Error, context?: Record<string, any>) {
    this.addEntry({
      timestamp: this.formatTimestamp(),
      level: 'error',
      message,
      context,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : undefined
    });
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    this.info('Logs cleared');
    this.notifyListeners();
  }
}

// Create and export a single instance
export const logger = Logger.getInstance();

// Initialize logger when the module loads
if (typeof window !== 'undefined') {
  logger.info('Logger module loaded', {
    timestamp: new Date().toISOString(),
    userAgent: window.navigator.userAgent,
    devMode: new URLSearchParams(window.location.search).get('devMode') === 'true'
  });
}